import { FileText, MapPin, NotepadText, Sprout, User, Wheat } from "lucide-react";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate } from "react-router-dom";
import { TimeIntervalDialog } from "@/components/Planning/components/TimeIntervalDialog";
import { CompleteTaskDialog } from "@/components/Planning/components/CompleteTaskDialog";
import { MapDialog } from "@/components/Planning/components/MapDialog";
import { CancelTaskDialog } from "@/components/Planning/components/CancelTaskDialog";
import { SamplesDialog } from "@/components/SamplesDialog";
import { formatDisplayDate } from "@/utils/dateFormat";
import { formatGps, parseGps } from "@/utils/gps";
import { getTaskBadge } from "@/utils/taskBadge";
import { getMineInfo } from "@/utils/taskLogic";
import { triStateValue } from "@/utils/triState";
import TaskDateLines from "@/components/TaskDateLines";
import { CO_LABEL } from "@/utils/contractLabel";

// A lighter "corner label" pill than the solid status badge, so the two don't read as the same
// kind of signal
const MINE_BADGE_STYLE = "border-blue-300 bg-blue-100 text-blue-700 dark:border-blue-700 dark:bg-blue-900/60 dark:text-blue-300";

// The old system put "CANCELLED" (and the like) in a trial's address field to mark it cancelled.
// That is no address: the card shows it as a badge instead.
const CANCELLED_MARKER = /^\W*(last\s+ass\s+)?cancel(l?ed)?\W*$/i;

const EMPTY = "—";

// One "label: value" line of the info section. Long values are cut off with "…" and shown in
// full on hover, so nothing overlaps whatever the card's width.
function InfoRow({ label, value, children }) {
  const text = value === null || value === undefined || value === "" ? EMPTY : String(value);
  return (
    <>
      <dt className="text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="min-w-0 truncate text-gray-800 dark:text-gray-200" title={text}>{children ?? text}</dd>
    </>
  );
}

// One line of the task box: icon, label and value (cut off like InfoRow)
function TaskLine({ icon, label, value, testId }) {
  return (
    <div className="grid min-w-0 grid-cols-[auto_auto_minmax(0,1fr)] items-center gap-x-1.5 text-sm">
      <span className="text-gray-500 dark:text-gray-400" aria-hidden="true">{icon}</span>
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span className="min-w-0 truncate" title={value} data-testid={testId}>{value}</span>
    </div>
  );
}

/**
 * A task on the dashboard. Every card has the same structure and height (TaskList's grid), with
 * room kept for what a task may not have, so the cards line up: header (trial no + status, then
 * role), info (customer, CO, protocol, address, GPS), the task itself in a box in its status
 * colour (the card's focus), badges, and the actions at the bottom.
 */
export function TaskCardSmall({ trial, task, onTaskComplete, onTaskCancel, currentUserId, showAssignee = false }) {
  const navigate = useNavigate();
  const location = useLocation(); // the trial's Back returns here

  const badge = getTaskBadge(task);
  const mineInfo = getMineInfo(trial, task, currentUserId);
  // With everyone's tasks shown ("Mine only" off): whom the task is assigned to
  const assignees = (Array.isArray(task?.assignees) ? task.assignees : []).map((a) => a?.name || a?.initials).filter(Boolean);
  const position = parseGps(trial?.PosGPSLatitude, trial?.PosGPSLongitude);
  const address = trial?.TrialAddress && !CANCELLED_MARKER.test(trial.TrialAddress) ? trial.TrialAddress : null;
  const markedCancelled = !!trial?.TrialAddress && CANCELLED_MARKER.test(trial.TrialAddress);
  const crop = trial?.crop?.name ? `${trial.crop.name}${trial.crop.description ? ` – ${trial.crop.description}` : ""}` : EMPTY;
  // The task's own BBCH, else the trial's latest, with the done date it was recorded on (utils/taskLogic.js)
  const cardBBCH = task?.cardBBCH;
  const bbch = cardBBCH ? (cardBBCH.doneDate ? `${cardBBCH.bbch} @ ${formatDisplayDate(cardBBCH.doneDate)}` : cardBBCH.bbch) : EMPTY;

  return (
    <Card className="flex h-full w-full min-w-0 flex-col gap-3 py-4" data-testid="task-card">
      <CardHeader className="block space-y-1 px-4">
        {/* Trial no, then the status below it: both wrap onto more lines, nothing is cut off */}
        <div className="flex min-w-0 flex-col items-start gap-1">
          <CardTitle className="min-w-0 max-w-full text-sm font-bold [overflow-wrap:anywhere]">
            {trial?.trialno || "Untitled Trial"}
          </CardTitle>
          <Badge
            className={`max-w-full justify-start whitespace-normal text-left [overflow-wrap:anywhere] ${badge.color}`}
            title={badge.detail}
            data-status={badge.status}
          >
            {badge.text}
          </Badge>
        </div>
        {/* Role or assignment - the line is kept when there is none, so the cards line up */}
        {/* A flex line of fixed height, not a clipped text line: the pill below keeps its whole shape */}
        <div className="flex h-6 min-w-0 items-center" data-testid="task-card-role">
          {showAssignee ? (
            <span className="flex min-w-0 items-center gap-1.5">
              {/* The same icon size and colour as the task box's lines */}
              <User className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" data-testid="task-card-assignee-icon" />
              <span data-testid="task-card-assignee" className="min-w-0 truncate text-xs text-gray-600 dark:text-gray-300" title={assignees.join(', ') || undefined}>
                {assignees.length ? <>Assigned to: <span className="font-semibold">{assignees.join(', ')}</span></> : <span className="italic">Unassigned</span>}
              </span>
            </span>
          ) : mineInfo.badgeText && (
            <span
              data-testid="task-card-mine"
              className={`inline-flex shrink-0 items-center rounded-full border px-2 py-[3px] text-[10px] font-semibold uppercase leading-none tracking-wide ${MINE_BADGE_STYLE}`}
            >
              {mineInfo.badgeText}
            </span>
          )}
        </div>
      </CardHeader>

      <CardContent className="flex min-w-0 flex-1 flex-col gap-3 px-4">
        {/* The trial (not internal ids) */}
        <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-0.5 text-xs">
          <InfoRow label="Customer" value={trial?.customer?.name} />
          <InfoRow label={CO_LABEL} value={trial?.project?.contract?.co} />
          <InfoRow label="Protocol No" value={trial?.project?.protocolno} />
          <InfoRow label="Address" value={address} />
          <InfoRow label="GPS" value={position ? formatGps(trial.PosGPSLatitude, trial.PosGPSLongitude) : null}>
            {position ? (
              <MapDialog latitude={position.lat} longitude={position.lng} trialName={trial.trialno}>
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                  {formatGps(trial.PosGPSLatitude, trial.PosGPSLongitude)}
                </span>
              </MapDialog>
            ) : undefined}
          </InfoRow>
        </dl>

        {/* The task itself - the card's focus, framed in its status colour */}
        <div
          data-testid="task-box"
          data-status={badge.status}
          className={`flex flex-1 flex-col gap-1 rounded-md border p-3 ${badge.borderColor} ${badge.surfaceColor}`}
        >
          <div className="min-w-0 truncate text-sm font-bold" title={task?.task}>{task?.task || "N/A"}</div>
          {/* The description with an icon like Crop, Interval and BBCH below; the line is kept when there is none */}
          <div className="flex min-h-4 min-w-0 items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300" title={task?.description || undefined}>
            {task?.description ? (
              <>
                <FileText className="h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" data-testid="task-card-description-icon" />
                <span className="min-w-0 truncate" data-testid="task-card-description">{task.description}</span>
              </>
            ) : null}
          </div>
          <TaskLine icon={<Wheat className="h-4 w-4" />} label="Crop" value={crop} />
          {/* Always both: the customer's Time interval (locked), then Agrolab's Planned date */}
          <TaskDateLines task={task} />
          <TaskLine icon={<Sprout className="h-4 w-4" />} label="BBCH" value={bbch} testId="task-card-bbch" />
        </div>

        {/* Badges on one line of fixed height (kept when there are none) */}
        <div className="flex h-6 items-center gap-1 overflow-hidden" data-testid="task-card-badges">
          {/* Yes: green; To be decided: yellow, with a "?" */}
          {['TGW', 'NIT'].map((field) => {
            const value = triStateValue(trial?.[field]);
            if (value === 1) return <Badge key={field} className="bg-green-500">{field}</Badge>;
            if (value === 2) return <Badge key={field} className="bg-yellow-500" title={`${field}: to be decided`}>{field}?</Badge>;
            return null;
          })}
          {trial?.HLLWonly ? <Badge className="bg-green-500">HLW only</Badge> : null}
          {markedCancelled ? (
            <Badge className="bg-gray-400" title={`The old system's address field says "${trial.TrialAddress}"`}>Marked cancelled</Badge>
          ) : null}
        </div>
      </CardContent>

      {trial?.id && (
        <CardFooter className="mt-auto flex flex-wrap justify-start gap-2 px-4">
          <CompleteTaskDialog trial={trial} task={task} onTaskComplete={onTaskComplete} />
          <Button variant="outline" aria-label="Open trial" title="Open trial" onClick={() => navigate(`/trials/${trial.id}`, { state: { from: location.pathname + location.search } })}>
            <NotepadText className="h-4 w-4" />
          </Button>
          <TimeIntervalDialog trial={trial} task={task} onTaskComplete={onTaskComplete} />
          <SamplesDialog trial={trial} onSaved={() => onTaskComplete && onTaskComplete()} />
          <CancelTaskDialog trialId={trial.id} task={task} onTaskCancel={onTaskCancel} />
        </CardFooter>
      )}
    </Card>
  );
}
